import {
    Building2,
    Check,
    Crown,
    Sparkles,
    TrendingUp,
    Users,
    Zap,
} from "lucide-react";
import { SegmentSwitch } from "../../components/ui/segmentSwitch";
import { cn } from "../../utils/tailwindUtils";

/** Display order for the four standard tiers. */
export const PLAN_ORDER = ["Core", "Growth", "Professional", "Enterprise"];

const PLAN_META = {
    Core: {
        icon: Building2,
        accent: "from-slate-500/15 to-slate-500/5",
        badge: null,
    },
    Growth: {
        icon: TrendingUp,
        accent: "from-emerald-500/15 to-emerald-500/5",
        badge: "Payroll ready",
    },
    Professional: {
        icon: Zap,
        accent: "from-blue-500/15 to-blue-500/5",
        badge: "Operations",
    },
    Enterprise: {
        icon: Crown,
        accent: "from-amber-500/20 to-sidebar-primary/10",
        badge: "Full platform",
    },
};

const MODULE_LABELS = {
    home: "Dashboard",
    administrator: "Administrator",
    master: "Master data",
    configuration: "Configuration",
    employee: "Employee HR",
    attendance: "Attendance",
    reports: "Reports",
    payroll: "Payroll & TDS",
    biometric: "Biometric sync",
    asset_management: "Asset management",
    rostering: "Shift rostering",
    calendar: "HR calendar",
    training: "Training (NABH)",
    appraisal: "Appraisal",
    data_migration: "Data migration",
    mobile: "Mobile app",
    erp: "ERP integration",
};

const formatPrice = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const amount = Number(value);
    if (Number.isNaN(amount)) return String(value);
    return `₹${amount.toLocaleString("en-IN")}`;
};

const formatModuleLabel = (key) =>
    MODULE_LABELS[key] ||
    String(key || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

export function sortPlans(plans) {
    if (!Array.isArray(plans)) return [];
    return [...plans].sort((a, b) => {
        const ai = PLAN_ORDER.indexOf(a?.packege_name);
        const bi = PLAN_ORDER.indexOf(b?.packege_name);
        const aRank = ai === -1 ? 999 : ai;
        const bRank = bi === -1 ? 999 : bi;
        return aRank - bRank;
    });
}

export function defaultPlanId(plans) {
    const sorted = sortPlans(plans);
    const growth = sorted.find((p) => p.packege_name === "Growth");
    const core = sorted.find((p) => p.packege_name === "Core");
    return growth?.id ?? core?.id ?? sorted[0]?.id ?? null;
}

function PlanCard({ plan, selected, billingCycle, onSelect }) {
    const meta = PLAN_META[plan.packege_name] || PLAN_META.Core;
    const Icon = meta.icon;
    const price = billingCycle === "yearly" ? plan.price_per_year : plan.price_per_month;
    const monthlyEquivalent =
        billingCycle === "yearly" && plan.price_per_year
            ? Math.round(Number(plan.price_per_year) / 12)
            : null;
    const yearlySavings =
        billingCycle === "yearly" &&
        plan.price_per_month &&
        plan.price_per_year &&
        plan.price_per_month * 12 > plan.price_per_year
            ? Math.round((1 - plan.price_per_year / (plan.price_per_month * 12)) * 100)
            : null;

    const modules = Array.isArray(plan.modules) ? plan.modules : [];
    const features = Array.isArray(plan.features) ? plan.features : [];
    const unlimited = plan.default_user_count == null;
    const userCapLabel = unlimited
        ? "Unlimited employees"
        : `Up to ${plan.default_user_count} employees`;

    return (
        <button
            type="button"
            onClick={() => onSelect(plan.id)}
            className={cn(
                "group relative flex flex-col text-left rounded-2xl border transition-all duration-200",
                "p-5 min-h-[420px] bg-card shadow-sm hover:shadow-md",
                selected
                    ? "border-sidebar-primary ring-2 ring-sidebar-primary/35 shadow-md scale-[1.01]"
                    : "border-border hover:border-sidebar-primary/40",
            )}
        >
            <div
                className={cn(
                    "absolute inset-x-0 top-0 h-24 rounded-t-2xl bg-gradient-to-b pointer-events-none",
                    meta.accent,
                )}
            />

            <div className="relative flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2.5 min-w-0">
                    <span
                        className={cn(
                            "w-10 h-10 rounded-xl grid place-items-center flex-shrink-0 border",
                            selected
                                ? "bg-sidebar-primary text-white border-sidebar-primary"
                                : "bg-background/80 border-border text-muted-foreground",
                        )}
                    >
                        <Icon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                        <div className="font-semibold text-base text-foreground truncate">
                            {plan.packege_name || "Plan"}
                        </div>
                        {meta.badge && (
                            <span className="inline-block mt-0.5 text-[10px] font-medium uppercase tracking-wide text-sidebar-primary">
                                {meta.badge}
                            </span>
                        )}
                    </div>
                </div>
                <span
                    className={cn(
                        "w-6 h-6 rounded-full border grid place-items-center flex-shrink-0 transition-colors",
                        selected
                            ? "bg-sidebar-primary border-sidebar-primary text-white"
                            : "border-border bg-background/80 group-hover:border-sidebar-primary/50",
                    )}
                >
                    {selected && <Check className="h-3.5 w-3.5" />}
                </span>
            </div>

            <div className="relative mb-3">
                <div className="flex items-baseline gap-1 flex-wrap">
                    <span className="text-2xl font-bold text-foreground tracking-tight">
                        {formatPrice(price)}
                    </span>
                    <span className="text-sm text-muted-foreground">
                        /{billingCycle === "yearly" ? "year" : "month"}
                    </span>
                </div>
                {monthlyEquivalent != null && billingCycle === "yearly" && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                        ≈ {formatPrice(monthlyEquivalent)}/month billed annually
                    </p>
                )}
                {yearlySavings != null && yearlySavings > 0 && billingCycle === "yearly" && (
                    <span className="inline-block mt-1.5 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
                        Save {yearlySavings}% vs monthly
                    </span>
                )}
            </div>

            <div className="relative flex items-center gap-1.5 mb-3">
                <Users className="h-3.5 w-3.5 text-sidebar-primary flex-shrink-0" />
                <span className="text-xs font-medium text-foreground">{userCapLabel}</span>
                <span className="text-[10px] text-muted-foreground ml-auto">
                    {modules.length} module{modules.length === 1 ? "" : "s"}
                </span>
            </div>

            {plan.packege_description && (
                <p className="relative text-xs text-muted-foreground leading-relaxed mb-3 line-clamp-2">
                    {plan.packege_description}
                </p>
            )}

            {features.length > 0 && (
                <ul className="relative space-y-1.5 mb-4 flex-1">
                    {features.map((feature) => (
                        <li
                            key={feature}
                            className="flex items-start gap-2 text-xs text-foreground/90 leading-snug"
                        >
                            <Check className="h-3.5 w-3.5 text-sidebar-primary flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                        </li>
                    ))}
                </ul>
            )}

            {modules.length > 0 && (
                <div className="relative pt-3 border-t border-border/80 mt-auto">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-2">
                        Modules included
                    </p>
                    <div className="flex flex-wrap gap-1">
                        {modules.map((mod) => (
                            <span
                                key={mod}
                                className="text-[10px] px-1.5 py-0.5 rounded-md bg-muted/80 text-muted-foreground"
                            >
                                {formatModuleLabel(mod)}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </button>
    );
}

export default function PlanSelector({
    plans,
    planId,
    billingCycle,
    loading,
    onPlanChange,
    onBillingCycleChange,
}) {
    const sortedPlans = sortPlans(plans);
    const selectedPlan = sortedPlans.find((p) => p.id === planId);

    return (
        <div className="space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <p className="text-sm text-muted-foreground">
                        Pick the tier that fits your team. You can upgrade later via support.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5 text-sidebar-primary" />
                        Grievance, AI Copilot &amp; integrations included on every plan.
                    </p>
                </div>
                <SegmentSwitch
                    options={[
                        { value: "monthly", label: "Monthly" },
                        { value: "yearly", label: "Yearly" },
                    ]}
                    value={billingCycle}
                    onChange={onBillingCycleChange}
                />
            </div>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {PLAN_ORDER.map((name) => (
                        <div
                            key={name}
                            className="h-[420px] rounded-2xl bg-muted/50 animate-pulse border border-border"
                        />
                    ))}
                </div>
            ) : sortedPlans.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border bg-muted/30 p-8 text-center">
                    <p className="text-sm text-muted-foreground">
                        No active plans found. Run{" "}
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">python manage.py seed_plans</code>{" "}
                        on the backend, then refresh.
                    </p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                        {sortedPlans.map((plan) => (
                            <PlanCard
                                key={plan.id}
                                plan={plan}
                                selected={planId === plan.id}
                                billingCycle={billingCycle}
                                onSelect={onPlanChange}
                            />
                        ))}
                    </div>

                    {selectedPlan && (
                        <div className="rounded-xl border border-sidebar-primary/25 bg-sidebar/5 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div className="text-sm">
                                <span className="text-muted-foreground">Selected: </span>
                                <span className="font-semibold text-foreground">
                                    {selectedPlan.packege_name}
                                </span>
                                <span className="text-muted-foreground">
                                    {" "}
                                    —{" "}
                                    {selectedPlan.default_user_count == null
                                        ? "unlimited seats"
                                        : `${selectedPlan.default_user_count} employee seats`}
                                    , billed {billingCycle}
                                </span>
                            </div>
                            <div className="text-sm font-semibold text-sidebar-primary">
                                {formatPrice(
                                    billingCycle === "yearly"
                                        ? selectedPlan.price_per_year
                                        : selectedPlan.price_per_month,
                                )}
                                <span className="text-muted-foreground font-normal">
                                    /{billingCycle === "yearly" ? "year" : "mo"}
                                </span>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
